import api from './api';

/**
 * HOW LONG A FULL COMPANY-FILE WALK IS ALLOWED TO TAKE.
 *
 * The api client's global timeout is 60s, which is right for every ordinary request and wrong for
 * these three: each reads every customer, every open invoice, every credit memo and every payment
 * ever recorded, across dozens of paginated Intuit calls. When the walk outlasts the timeout the
 * browser aborts a request the server is still working on, the operator is told the page failed,
 * and - because the abort does not un-spend it - the route's one-request-per-minute allowance is
 * already gone, so the obvious retry answers 429. A slow read then presents as a broken page that
 * stays broken for a minute.
 *
 * Applied per request rather than by raising the global: nothing else in this admin should wait
 * three minutes before deciding a request has failed.
 */
const SCAN_TIMEOUT_MS = 180000;

/**
 * Weekly customer account statements (WQ-260827). ADMIN ONLY - every route below is @Roles('admin')
 * server-side, and the page is admin-guarded automatically because '/statements' is absent from
 * REP_ALLOWED_PATHS.
 *
 * NOTHING HERE SENDS ANYTHING. There is no send call because no send path exists yet; the backend
 * has a test that fails the build if one appears before the whole plan is reviewed and merged.
 */
const statementService = {
  /**
   * The statements themselves: every open document per customer, aged, with totals.
   * `asOf` (YYYY-MM-DD) ages as of a chosen date; omitted means today in Eastern.
   */
  getPreview: (params) =>
    api.get('/admin/statements/preview', { params, timeout: SCAN_TIMEOUT_MS }),

  /**
   * One customer's statement as a real PDF.
   *
   * Fetched as a BLOB through the API client rather than opened with window.open, and that is not a
   * style choice: the admin app is on a different origin from the API and authenticates with a
   * bearer header, so a direct navigation carries no credential and would simply 401. The caller
   * gets bytes and decides whether to display or download them.
   */
  getStatementPdf: (customerId, asOf) =>
    api.get('/admin/statements/preview.pdf', {
      params: { customerId, asOf },
      responseType: 'blob',
      timeout: SCAN_TIMEOUT_MS,
    }),

  /** Every customer in the connected company file, and whether a credit-limit message is set. */
  listMessages: () => api.get('/admin/statements/messages'),

  /** The full stored text for one customer, for the editor to load. */
  // encodeURIComponent on every path segment. The value is a Mongo id from our own API today,
  // but a path built by concatenation lets a value containing ../ or ? re-point the request at a
  // different admin endpoint - carrying the bearer token and, on the PUT, a body.
  getMessage: (clinicId) => api.get(`/admin/statements/messages/${encodeURIComponent(clinicId)}`),

  /**
   * Save one customer's credit-limit message.
   *
   * ALWAYS sends the key explicitly. The server rejects a PUT that omits it, because omission used
   * to clear a customer's negotiated terms and answer success - reachable by accident from a form
   * whose value was still undefined.
   */
  saveMessage: (clinicId, message) =>
    api.put(`/admin/statements/messages/${encodeURIComponent(clinicId)}`, {
      message: message ?? '',
    }),

  /** Remove a customer's message entirely. */
  clearMessage: (clinicId) =>
    api.delete(`/admin/statements/messages/${encodeURIComponent(clinicId)}`),

  /** The suggested starting text, so an operator edits a real example rather than a blank box. */
  getTemplate: () => api.get('/admin/statements/message-template'),

  /**
   * What the weekly run WOULD do, and every reason a customer would be refused.
   *
   * READ-ONLY. This is GET /run-plan, not POST /run: it derives the decision with the same code the
   * run uses and returns it, without a claim being written or a transport being touched. The POST
   * is deliberately absent from this file - a client that cannot name the send route cannot call it
   * by accident, and the page has no button that could.
   */
  getRunPlan: (params) =>
    api.get('/admin/statements/run-plan', { params, timeout: SCAN_TIMEOUT_MS }),

  /** Past runs, newest first. Read-only. */
  listRuns: (limit) => api.get('/admin/statements/runs', { params: { limit } }),

  /** One past run, and what it decided for each customer. Read-only. */
  getRun: (runId) => api.get(`/admin/statements/runs/${encodeURIComponent(runId)}`),
};

export default statementService;
